/* eslint-disable react/no-unknown-property */
import * as THREE from "three";
import { extend } from "@react-three/fiber";
import { shaderMaterial, useTexture } from "@react-three/drei";

// Static replacement for AccumulativeShadows: the accumulated lightmap is
// exported once at dev time (scripts/bake-shadows.mjs) and replayed here
// through the exact same shader drei's SoftShadowMaterial uses, so the
// result is pixel-identical to the live bake - minus the multi-second
// startup cost of 30 accumulation passes and their shadow-shader compiles.
//
// The PNG stores the lightmap at HALF scale: the live target is HalfFloat
// and lit areas can exceed 1.0, which an 8-bit PNG would clip. Halving at
// export and doubling the ratios here compensates exactly:
//   color * (r/2) * (2*blend)        == color * r * blend
//   1 - (sum/2) / (alphaTest/2)      == 1 - sum / alphaTest
const BakedShadowMaterial = shaderMaterial(
  {
    color: new THREE.Color(),
    blend: 2.0,
    alphaTest: 0.75,
    opacity: 0,
    map: null,
  },
  `varying vec2 vUv;
   void main() {
     gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.);
     vUv = uv;
   }`,
  `varying vec2 vUv;
   uniform sampler2D map;
   uniform vec3 color;
   uniform float opacity;
   uniform float alphaTest;
   uniform float blend;
   void main() {
     vec4 sampledDiffuseColor = texture2D(map, vUv);
     gl_FragColor = vec4(color * sampledDiffuseColor.r * blend, max(0.0, (1.0 - (sampledDiffuseColor.r + sampledDiffuseColor.g + sampledDiffuseColor.b) / alphaTest)) * opacity);
     #include <tonemapping_fragment>
     #include <colorspace_fragment>
   }`,
);

extend({ BakedShadowMaterial });

export default function BakedShadows() {
  const map = useTexture("./textures/baked-shadows.png", (t) => {
    // Match the live render target's sampling exactly: no flip (rows were
    // written in render-target order), no color-space decode (raw lightmap
    // data), nearest filtering (the live target uses NearestFilter).
    t.flipY = false;
    t.colorSpace = THREE.NoColorSpace;
    t.minFilter = THREE.NearestFilter;
    t.magFilter = THREE.NearestFilter;
    t.generateMipmaps = false;
    t.needsUpdate = true;
  });

  // Same transform chain as the live version: outer group at y -0.01,
  // scale-100 unit plane rotated flat. Uniform values are the live
  // colorBlend=5 / alphaTest=0.9 doubled/halved per the note above.
  return (
    <group position={[0, -0.01, 0]}>
      <mesh scale={100} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry />
        <bakedShadowMaterial
          transparent
          depthWrite={false}
          toneMapped
          map={map}
          color="#1d121e"
          blend={10}
          alphaTest={0.45}
          opacity={1}
        />
      </mesh>
    </group>
  );
}
